import { Trans } from "@lingui/macro";
import { useCallback, useMemo, useRef, useState } from "react";
import {
  useCurrentRefinements,
  useRefinementList,
} from "react-instantsearch-core-v7";
import { DropdownOutlined } from "src/atoms/DropDown";
import {
  FilterContentWrapper,
  ResultsButton,
  ScrollWrapper,
} from "src/organisms/AlgoliaProductList/DesktopFilterVeiw/DesktopFilter.styled";
import { Box, Flex } from "@otrium/core";
import { useOnClickOutside } from "src/hooks/useOnClickOutside";
import { Checkbox } from "src/atoms/Checkbox";
import {
  sortByCategoryName,
  sortByCount,
} from "src/utils/product-listing-helpers";
import { InputTextSearch } from "../InputTextSearch";
import { debounce } from "src/utils";
import {
  FavoriteBrandsWrapper,
  GoodOnYouLabel,
  SearchWrapper,
} from "./Filter.styled";
import { BRAND_FACET, CONSCIOUS_FACET } from "src/utils/algolia";
import { useWishListBrands } from "src/hooks/useWishListBrands";
import { useNewIsLoggedIn } from "src/hooks/useIsLoggedIn";
import { useSegment } from "src/hooks/useSegment";
import { useHandleSearchParams } from "src/hooks/use-handle-search-params";

type BrandFilterProps =
  | {
      attribute: string;
      placeholder: string;
      type: "multi-select" | "multi-select-search";
      totalResults: number;
      isMultiColumn?: boolean;
      limit?: number;
      handleScrollToTop: () => void;
    } & (
      | {
          showSearch: true;
          searchPlaceholder: string;
        }
      | { showSearch?: false; searchPlaceholder?: undefined }
    );

function BrandFilter({
  attribute,
  placeholder,
  totalResults: total,
  showSearch,
  searchPlaceholder,
  isMultiColumn,
  limit = 1000,
  handleScrollToTop,
}: BrandFilterProps): JSX.Element | null {
  const { isLoggedIn, isSessionLoading } = useNewIsLoggedIn();
  const filterRef = useRef<HTMLDivElement>(null);
  const [isOpen, setOpen] = useState(false);
  const { handleSearchParams } = useHandleSearchParams();

  const { items, refine, searchForItems, canRefine } = useRefinementList({
    attribute,
    sortBy: attribute === BRAND_FACET ? sortByCount : sortByCategoryName,
    limit,
  });
  const { items: currentRefinements } = useCurrentRefinements({
    includedAttributes: [attribute],
  });
  const { segmentProductListFilterOpened } = useSegment();

  const { data: favBrandsData } = useWishListBrands(
    9999,
    0,
    !isSessionLoading && !isLoggedIn
  );
  const favoriteBrands = useMemo(() => {
    const favoriteBrandIds =
      favBrandsData?.wishlistBrands?.items.map((item) => item?.id) || [];

    return items
      .filter((item) => {
        const itemId = item.value.split("||")[0];

        return favoriteBrandIds?.includes(itemId);
      })
      .map((item) => item.value);
  }, [favBrandsData, items]);

  const favoriteBrandsSlug = useMemo(() => {
    return favoriteBrands.map((brand) => {
      const splitBrand = brand.split("||");
      return splitBrand.at(-1) || "";
    });
  }, [favoriteBrands]);

  const favoriteBrandsSelected = useMemo(
    () =>
      items
        .filter((item) => favoriteBrands.includes(item.value))
        .every((item) => item.isRefined),
    [favoriteBrands, items]
  );

  const toggleOpen = debounce(() => {
    setOpen(!isOpen);

    if (!isOpen) {
      void segmentProductListFilterOpened(attribute);
    }
  }, 100);

  useOnClickOutside(filterRef, toggleOpen, isOpen);

  const handleRefine = useCallback(
    (value: string) => {
      handleSearchParams({ facet: attribute, value, deletePage: true });

      refine(value);

      handleScrollToTop();
    },
    [attribute, refine, handleSearchParams, handleScrollToTop]
  );

  const toggleFavoriteBrands = useCallback(() => {
    handleSearchParams({
      facet: attribute,
      value: "",
      values: favoriteBrandsSlug,
      deletePage: true,
    });

    favoriteBrands.forEach((item) => {
      if (!item) {
        return;
      }

      refine(item);
    });

    handleScrollToTop();
  }, [
    favoriteBrands,
    favoriteBrandsSlug,
    attribute,
    refine,
    handleSearchParams,
    handleScrollToTop,
  ]);

  return (
    <DropdownOutlined opened={isOpen} disabled={!canRefine}>
      <DropdownOutlined.Button onClick={toggleOpen}>
        <span>{placeholder}</span>
      </DropdownOutlined.Button>
      {isOpen ? (
        <DropdownOutlined.Items>
          <Flex
            ref={filterRef}
            paddingBlockStart={3}
            paddingBlockEnd={3}
            flexDirection="column"
            maxHeight="inherit"
          >
            <SearchWrapper>
              {showSearch ? (
                <Box>
                  <InputTextSearch
                    placeholder={searchPlaceholder}
                    handleChange={(value) =>
                      searchForItems(value.currentTarget.value)
                    }
                    onClearSearch={() => searchForItems("")}
                  />
                </Box>
              ) : null}

              {favoriteBrands?.length ? (
                <FavoriteBrandsWrapper>
                  <Checkbox
                    name="Select my favourite brands"
                    checked={favoriteBrandsSelected}
                    onChange={toggleFavoriteBrands}
                  >
                    <span>
                      <Trans>Select my favourite brands</Trans>
                    </span>
                  </Checkbox>
                </FavoriteBrandsWrapper>
              ) : null}
            </SearchWrapper>

            <ScrollWrapper>
              <FilterContentWrapper
                as="ul"
                isMultiColumn={isMultiColumn && items.length > 1}
                count={Math.round(items.length / 2)}
              >
                {items.map((item) => {
                  const splitLabel = item.label.split("||");
                  const id = splitLabel[0];
                  const label = splitLabel[1];

                  return (
                    <Box as="li" key={`${attribute}-${id}`}>
                      <Checkbox
                        name={label}
                        checked={item.isRefined}
                        onChange={() => handleRefine(item.value)}
                      >
                        {label}
                      </Checkbox>
                    </Box>
                  );
                })}
              </FilterContentWrapper>
            </ScrollWrapper>

            {attribute === CONSCIOUS_FACET ? (
              <GoodOnYouLabel>
                <Trans>Provided by Good On You</Trans>®.{" "}
                <a href={`http://howwerateourbrands.otrium.com/`}>
                  <Trans>Learn how this is rated</Trans>
                </a>
              </GoodOnYouLabel>
            ) : null}

            <Flex variant="centered">
              <ResultsButton
                fullWidth
                variant="primary"
                onClick={toggleOpen}
                disabled={currentRefinements.length === 0 || total === 0}
              >
                <span>
                  {currentRefinements.length > 0 && total > 0 ? (
                    <Trans>Show {total} results</Trans>
                  ) : (
                    <Trans>Show results</Trans>
                  )}
                </span>
              </ResultsButton>
            </Flex>
          </Flex>
        </DropdownOutlined.Items>
      ) : null}
    </DropdownOutlined>
  );
}

export { BrandFilter };
